import { useState } from 'react';
import { cva } from 'class-variance-authority';

// Shared by ToggleGroup so individual group items use the same variant API.
export const toggleVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-transparent hover:bg-muted hover:text-muted-foreground",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-9 px-3",
        sm: "h-8 px-2",
        lg: "h-10 px-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface ToggleProps {
  enabled?: boolean;
  onChange?: (enabled: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function Toggle({ 
  enabled = false, 
  onChange, 
  disabled = false,
  size = 'md'
}: ToggleProps) {
  const [isEnabled, setIsEnabled] = useState(enabled);

  const handleToggle = () => {
    if (disabled) return;
    const newState = !isEnabled;
    setIsEnabled(newState);
    onChange?.(newState);
  };

  // Size configurations
  const sizeConfig = {
    sm: { width: '40px', height: '20px', dotSize: '14px', dotOffset: '3px' },
    md: { width: '48px', height: '24px', dotSize: '18px', dotOffset: '3px' },
    lg: { width: '56px', height: '28px', dotSize: '22px', dotOffset: '3px' }
  };

  const config = sizeConfig[size];

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isEnabled}
      onClick={handleToggle}
      disabled={disabled}
      style={{
        position: 'relative',
        width: config.width,
        height: config.height,
        backgroundColor: isEnabled 
          ? 'var(--accent)' 
          : disabled 
            ? 'var(--muted)' 
            : 'var(--border)',
        borderRadius: '9999px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s ease-in-out',
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: config.dotOffset,
          left: isEnabled 
            ? `calc(100% - ${config.dotSize} - ${config.dotOffset})` 
            : config.dotOffset,
          width: config.dotSize,
          height: config.dotSize,
          backgroundColor: 'white',
          borderRadius: '9999px',
          transition: 'left 0.2s ease-in-out',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
        }}
      />
    </button>
  );
}
